import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { Contact } from "types/Contact";
import { Customer } from "types/Customer";

type Props = {
  customer: Pick<Customer, "phone" | "email" | "assignedUser"> & {
    contacts?: Contact[];
  };
  /** The customer's main address as one line. */
  address: string;
};

const PhoneAndEmail = ({ phone, email }: { phone?: string; email?: string }) => (
  <div className="flex flex-wrap gap-x-4">
    {phone && (
      <a href={`tel:${phone}`} className="inline-flex min-h-11 items-center">
        {phone}
      </a>
    )}
    {email && (
      <a href={`mailto:${email}`} className="inline-flex min-h-11 items-center">
        {email}
      </a>
    )}
  </div>
);

/**
 * Who to call or write, above the customer card's tabs. The right column holds
 * the same data (and the place to edit it), but it is stacked under the tab
 * content, far from where a visit or a call starts, whenever the page is under
 * about 740 px wide: a window under 784 px, or 1024-1119 px with the sidebar and
 * the online rail taking their share (measured with Polaris' Layout in Chrome).
 * Outside those windows the column stands beside the tabs and this block would
 * only repeat it.
 */
export default function CustomerQuickContact({ customer, address }: Props) {
  const { t } = useTranslation();
  const people = (customer.contacts || []).filter((contact) => !contact.isDisabled);

  const hasAnything =
    address || customer.assignedUser || customer.phone || customer.email || people.length > 0;
  if (!hasAnything) return null;

  return (
    <div
      className="mb-4 rounded-sm border border-[#E6E1DB] bg-white px-3 py-1 text-sm min-[784px]:max-[1023px]:hidden min-[1120px]:hidden"
      data-testid="customer-quick-contact"
    >
      {address && <div className="py-1 text-gray-600">{address}</div>}
      {customer.assignedUser && (
        <div className="py-1 text-gray-600">
          {t("assigned_user")}: {customer.assignedUser.name}
        </div>
      )}
      <PhoneAndEmail phone={customer.phone} email={customer.email} />
      {people.length > 0 && (
        <details className="group border-t border-[#F2F1EF]">
          <summary className="flex min-h-11 cursor-pointer items-center justify-between font-medium text-gray-900">
            {t("contacts")} ({people.length})
            <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
          </summary>
          <ul className="pb-2">
            {people.map((person) => (
              <li key={person.id} className="py-1">
                <div className="font-medium text-gray-900">
                  {[person.name, person.position].filter(Boolean).join(" · ")}
                </div>
                <PhoneAndEmail phone={person.phone} email={person.email} />
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
